import { useState, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { omcTendersData, OmcTender } from '@/mocks/tendersData';
import tendersHero from '@/assets/apof7zapof7zapof.jpg';

const OmcTenders = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEsy, setSelectedEsy] = useState<string>('All');

  const esyList = ['All', '2025-26', '2024-25', '2023-24', '2022-23', '2021-22', '2020-21'];

  const filteredTenders = omcTendersData.filter((tender) => {
    const matchesSearch =
      tender.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tender.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tender.date.includes(searchQuery) ||
      tender.esy.includes(searchQuery);

    const matchesEsy = selectedEsy === 'All' || tender.esy === selectedEsy;

    return matchesSearch && matchesEsy;
  });

  const getEsyBadgeStyle = (esy: string) => {
    switch (esy) {
      case '2025-26':
        return 'bg-bteal/10 text-bteal border-bteal/20';
      case '2024-25':
        return 'bg-bgreen/10 text-bgreen-dark border-bgreen/20';
      case '2023-24':
        return 'bg-amber-50 text-amber-900 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="font-jakarta bg-white min-h-screen flex flex-col">
      <Navbar />
      <main ref={pageRef} className="flex-1">
        {/* Hero Section */}
        <section className="pt-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pt-12 pb-10 text-center">
            <div data-reveal="fade" data-delay="1" className="mb-4">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">
                Procurement &amp; Allocation
              </span>
            </div>
            <h1 data-reveal data-delay="2" className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight mb-4">
              OMC Ethanol Tenders
            </h1>
            <p data-reveal data-delay="3" className="text-ink-muted text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Official tender documents, Quantity Bids, Expressions of Interest (EOI), and Corrigendum notifications issued by Oil Marketing Companies (IOCL, BPCL, HPCL, Nayara &amp; Reliance) for Ethanol Supply Years.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div data-reveal="scale" data-delay="4" className="rounded-2xl overflow-hidden w-full shadow-lg" style={{ height: '280px' }}>
              <img
                src={tendersHero}
                alt="OMC Ethanol Tenders"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="h-px bg-gray-100 mt-10" />
        </section>

        {/* Main Content & Tenders Table */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {/* Search and Filters Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              {/* Search Box */}
              <div className="relative w-full md:w-80">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  type="text"
                  placeholder="Search tenders by keyword or date..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-xs sm:text-sm text-ink focus:outline-none focus:border-bteal focus:ring-2 focus:ring-bteal/10 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-circle-fill" />
                  </button>
                )}
              </div>

              {/* ESY Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
                <span className="text-xs font-semibold text-ink-muted mr-1 shrink-0">Filter by ESY:</span>
                {esyList.map((esy) => (
                  <button
                    key={esy}
                    onClick={() => setSelectedEsy(esy)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 whitespace-nowrap cursor-pointer ${
                      selectedEsy === esy
                        ? 'bg-bteal text-white shadow-xs'
                        : 'bg-surface text-ink-muted hover:bg-gray-100 hover:text-ink border border-gray-100'
                    }`}
                  >
                    {esy === 'All' ? 'All Supply Years' : `ESY ${esy}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between mb-6 text-xs text-ink-muted">
              <span>Showing <strong>{filteredTenders.length}</strong> of {omcTendersData.length} tender documents</span>
              {selectedEsy !== 'All' && (
                <button onClick={() => setSelectedEsy('All')} className="text-bteal font-semibold hover:underline">
                  Reset Filter
                </button>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface text-ink-muted text-xs font-bold uppercase tracking-wider border-b border-gray-200/80">
                    <th className="py-4 px-6 w-20">Sr. No.</th>
                    <th className="py-4 px-6">Tender Details &amp; Scope</th>
                    <th className="py-4 px-6 w-36">Tender Date</th>
                    <th className="py-4 px-6 w-36">ESY Year</th>
                    <th className="py-4 px-6 w-36 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredTenders.length > 0 ? (
                    filteredTenders.map((tender) => (
                      <tr key={tender.id} className="hover:bg-teal-900/[0.015] transition-colors group">
                        <td className="py-4 px-6 font-bold text-ink-muted text-xs">
                          #{tender.srNo}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-file-pdf-2-line text-lg" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-ink text-sm leading-snug group-hover:text-bteal transition-colors">
                                {tender.title}
                              </h3>
                              <div className="flex items-center gap-2 mt-1.5 text-xs text-ink-muted">
                                <span className="inline-flex items-center gap-1 font-medium bg-gray-100 px-2 py-0.5 rounded text-[11px]">
                                  <i className="ri-map-pin-line text-[11px]" />
                                  {tender.location}
                                </span>
                                <span>&bull;</span>
                                <span className="font-medium text-bteal">Volume: {tender.quantity}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap text-ink font-medium text-xs">
                          <i className="ri-calendar-line text-gray-400 mr-1.5" />
                          {tender.date}
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap">
                          <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold border ${getEsyBadgeStyle(tender.esy)}`}>
                            ESY {tender.esy}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right whitespace-nowrap">
                          <a
                            href={tender.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-bteal text-bteal text-xs font-semibold hover:bg-bteal hover:text-white transition-all duration-150 cursor-pointer shadow-2xs"
                          >
                            <i className="ri-download-line text-xs" />
                            <span>Download PDF</span>
                          </a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-ink-muted text-sm">
                        <i className="ri-search-line text-3xl text-gray-300 block mb-2" />
                        No tenders found matching your query or selected ESY filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile / Tablet Cards View */}
            <div className="lg:hidden space-y-4">
              {filteredTenders.length > 0 ? (
                filteredTenders.map((tender) => (
                  <div key={tender.id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs hover:border-bteal/30 transition-all">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <span className="text-xs font-bold text-ink-muted bg-gray-100 px-2.5 py-1 rounded-md">
                        #{tender.srNo}
                      </span>
                      <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${getEsyBadgeStyle(tender.esy)}`}>
                        ESY {tender.esy}
                      </span>
                    </div>

                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-pdf-2-line text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-ink text-sm leading-snug">
                          {tender.title}
                        </h3>
                        <p className="text-xs text-ink-muted mt-1">
                          Location: <strong className="text-ink">{tender.location}</strong>
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                      <span className="text-xs text-ink-muted flex items-center gap-1">
                        <i className="ri-calendar-line text-gray-400" />
                        {tender.date}
                      </span>
                      <a
                        href={tender.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-colors"
                      >
                        <i className="ri-download-line text-xs" />
                        <span>Download PDF</span>
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center bg-surface rounded-2xl border border-gray-200 text-ink-muted text-sm">
                  No tenders found matching your query or selected filter.
                </div>
              )}
            </div>

            {/* Tender Support & OMC Contacts Section */}
            <div className="mt-16 space-y-8">
              <div className="text-center md:text-left border-b border-gray-200 pb-4">
                <span className="text-[11px] font-bold text-bgreen tracking-widest uppercase">Support &amp; Assistance</span>
                <h2 className="text-2xl md:text-3xl font-bold text-ink mt-1">OMC Tender Contacts &amp; E-Procurement Helpdesk</h2>
                <p className="text-xs sm:text-sm text-ink-muted mt-1">Direct contact details for Central Procurement Organization, OMC officers, and C1 India technical support team.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* 1. Central Procurement Organization (BPCL) */}
                <div className="bg-surface rounded-2xl p-6 border border-gray-200/90 flex flex-col justify-between shadow-2xs hover:border-bteal/30 transition-all">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-bteal/10 text-bteal flex items-center justify-center font-bold shrink-0">
                        <i className="ri-building-2-line text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-ink text-sm sm:text-base leading-snug">Central Procurement Organization (Mktg)</h3>
                        <p className="text-[11px] font-semibold text-bteal">Bharat Petroleum Corporation Ltd</p>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="mb-5 p-3.5 rounded-xl bg-white border border-gray-100 text-xs space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block">Official Address</span>
                      <p className="text-ink leading-relaxed font-medium">
                        Procurement Mgr. / GM Procurement,<br />
                        Central Procurement Organization (Mktg),<br />
                        Bharat Petroleum Corporation Ltd,<br />
                        &apos;A&apos; Installation, Sewree Fort Road,<br />
                        Sewree East, Mumbai &ndash; 400 015
                      </p>
                    </div>

                    {/* Contacts */}
                    <div className="space-y-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block">Contact Persons</span>
                      
                      <div className="p-3 rounded-xl bg-white border border-gray-100 text-xs space-y-1">
                        <p className="font-bold text-ink text-sm">Mr. Rajesh Gehlot <span className="font-normal text-ink-muted text-xs">(Procurement Manager)</span></p>
                        <p className="text-ink-muted flex items-center gap-1.5 pt-0.5">
                          <i className="ri-phone-line text-bteal" />
                          <a href="tel:02224176416" className="hover:text-bteal">022-24176416</a> / <a href="tel:9950100035" className="hover:text-bteal">9950100035</a>
                        </p>
                        <p className="text-ink-muted flex items-center gap-1.5 truncate">
                          <i className="ri-mail-line text-bteal" />
                          <a href="mailto:rajeshgehlot@bharatpetroleum.in" className="hover:text-bteal font-medium text-bteal truncate">rajeshgehlot@bharatpetroleum.in</a>
                        </p>
                      </div>

                      <div className="p-3 rounded-xl bg-white border border-gray-100 text-xs space-y-1">
                        <p className="font-bold text-ink text-sm">Mr. Sanson T Koleth <span className="font-normal text-ink-muted text-xs">(GM Procurement)</span></p>
                        <p className="text-ink-muted flex items-center gap-1.5 pt-0.5">
                          <i className="ri-phone-line text-bteal" />
                          <a href="tel:02224176133" className="hover:text-bteal">022-24176133</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Oil Marketing Companies Contacts */}
                <div className="bg-surface rounded-2xl p-6 border border-gray-200/90 flex flex-col justify-between shadow-2xs hover:border-bteal/30 transition-all">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-bgreen/15 text-bgreen-dark flex items-center justify-center font-bold shrink-0">
                        <i className="ri-contacts-book-2-line text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-ink text-sm sm:text-base leading-snug">OMC Contact Persons</h3>
                        <p className="text-[11px] font-semibold text-bgreen-dark">For Tender Terms &amp; Conditions</p>
                      </div>
                    </div>

                    <div className="space-y-2.5">
                      {[
                        { omc: "BPCL", name: "Mr. Vineet Bhutani", email: "bhutaniv@bharatpetroleum.in", phone: "9409305433" },
                        { omc: "HPCL", name: "Mr. C.P Mishra", email: "chandra.mishra@hpcl.in", phone: "7895808232" },
                        { omc: "IOCL", name: "Mr. Rohit Maheswari", email: "rmaheshwari@indianoil.in", phone: "9929968296" },
                        { omc: "MRPL", name: "Mr. Ganesh Kumar C", email: "ganeshkumar@mrpl.co.in", phone: "9535379610" },
                        { omc: "NRL", name: "Mr. Suman Goswami", email: "suman.goswami@nrl.co.in", phone: "9435738708" },
                      ].map((item) => (
                        <div key={item.omc} className="p-3 rounded-xl bg-white border border-gray-100 flex items-start justify-between gap-2 text-xs">
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-extrabold text-[10px] bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded uppercase">{item.omc}</span>
                              <span className="font-bold text-ink">{item.name}</span>
                            </div>
                            <p className="text-ink-muted text-[11px] flex items-center gap-1">
                              <i className="ri-mail-line text-bteal" />
                              <a href={`mailto:${item.email}`} className="hover:text-bteal font-medium text-bteal">{item.email}</a>
                            </p>
                          </div>
                          <a href={`tel:${item.phone}`} className="font-semibold text-ink hover:text-bteal flex items-center gap-1 shrink-0 bg-gray-50 px-2 py-1 rounded">
                            <i className="ri-phone-line text-bteal text-xs" />
                            <span>{item.phone}</span>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 3. M/s C1 India Technical Support */}
                <div className="bg-surface rounded-2xl p-6 border border-gray-200/90 flex flex-col justify-between shadow-2xs hover:border-bteal/30 transition-all">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-100/80 text-amber-900 flex items-center justify-center font-bold shrink-0">
                        <i className="ri-customer-service-2-line text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-ink text-sm sm:text-base leading-snug">M/s C1 India Support</h3>
                        <p className="text-[11px] font-semibold text-amber-800">E-Tender, E-EOI &amp; Portal Technical Desk</p>
                      </div>
                    </div>

                    {/* Support Mail Pill */}
                    <div className="mb-4 p-3 rounded-xl bg-amber-50/70 border border-amber-200/80 flex items-center justify-between gap-2 text-xs">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 block">Support Email</span>
                        <a href="mailto:bpclsupport@c1india.com" className="font-bold text-bteal hover:underline text-xs sm:text-sm">bpclsupport@c1india.com</a>
                      </div>
                      <a href="mailto:bpclsupport@c1india.com" className="px-3 py-1 rounded-lg bg-bteal text-white text-[11px] font-semibold shrink-0">
                        Send Mail
                      </a>
                    </div>

                    {/* Help Desk List */}
                    <div className="space-y-2 mb-4">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block">Support Help Desk</span>
                      <div className="grid grid-cols-1 gap-1.5 text-xs">
                        {[
                          { name: "Gourav Panthi", phone: "+91 799313129" },
                          { name: "Pankaj Pal", phone: "+91 9643813220" },
                          { name: "Tanmay Baviskar", phone: "+91 8433884968" },
                          { name: "Hebrin George", phone: "+91 8921155218" },
                          { name: "Saranraj Naicker", phone: "+91-124-4302000 Ext: 110" },
                        ].map((h) => (
                          <div key={h.name} className="p-2.5 rounded-lg bg-white border border-gray-100 flex items-center justify-between text-xs">
                            <span className="font-semibold text-ink">{h.name}</span>
                            <a href={`tel:${h.phone.replace(/[^0-9+]/g, '')}`} className="font-medium text-ink-muted hover:text-bteal flex items-center gap-1">
                              <i className="ri-phone-line text-bteal" />
                              <span>{h.phone}</span>
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Escalation */}
                    <div className="p-3 rounded-xl bg-white border border-gray-100 text-xs">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 block mb-0.5">Escalation Contact</span>
                      <p className="font-bold text-ink">Sachin Toraskar <span className="font-normal text-ink-muted text-[11px]">(Asst. Manager)</span></p>
                      <p className="text-ink-muted text-[11px] flex items-center gap-1 mt-0.5">
                        <i className="ri-mail-line text-bteal" />
                        <a href="mailto:sachin.toraskar@c1india.com" className="hover:text-bteal font-medium text-bteal">sachin.toraskar@c1india.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OmcTenders;
